import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";
import { Preferences } from "@capacitor/preferences";
import { SplashScreen } from "@capacitor/splash-screen";
import { BundleInfo, CapacitorUpdater } from "@capgo/capacitor-updater";
import { Keyboard } from "@eventr/capacitor-keyboard";
import { spLogTags } from "@eventr/common";
import { useDrawerStore } from "@eventr/common-storybook";
import { isIosNativeNavigation } from "feat/nativeNavigation/platform";
import { routes } from "routing";
import { bs } from "../../state/bound.store";
import { flushStoredWrites } from "../../state/indexdbStorage";
import { useOverlayStore } from "../../state/overlayState/overlay.store";
import { captureSentryMessage } from "../../utils";
import { reactAppStage } from "../../utils/reactAppStage";
import { checkCalInitialPermissions } from "../calendar/calendarProvider";
import { recordOpenedUrl, recoverLaunchUrl } from "../deeplink/nativeLaunchUrl";
import { isDeeplinkInFlight, navigateToDeeplink } from "../deeplink/pendingDeeplink";
import { addPushListener } from "../push/pushProvider";
import "./draft-state-sync";
import { pullLatestDrafts } from "./draft-state-sync";
import { isNewerBundle } from "./isNewerBundle";
import { otaUpdatesEnabled } from "./otaUpdatesEnabled";

// Track permission request states
let backgroundTimer: NodeJS.Timeout | null = null;
const PERMISSION_THRESHOLD = 5000; // 5 seconds threshold to distinguish between permission dialog and actual background

// notifyAppReady() used to be called here. It now lives in ./notifyAppReady, imported as
// the very first thing in index.tsx, because this module is evaluated after ./App,
// ./data/apiClients and ./data/interseptors - a module-scope throw in any of those meant
// the call never happened and @capgo reverted the bundle after appReadyTimeout.
let data: BundleInfo | null = null;
let isSwitchingStage = false;

// Long enough for a cold boot on a slow device to reach its first view and hide the
// splash itself, short enough that a bundle which never boots leaves the user a blank
// tappable web view and bootSafety's recovery instead of an immortal white overlay.
const SWAP_SPLASH_SETTINGS = { autoHide: true, showDuration: 15_000 };

const backButtonMap: Record<string, string> = {
  [routes.home.path]: "exit",
  "/": "exit",
  [routes.auth.signIn.path]: "/",
};

/// Scroll issues on ios, solution to disable setScroll. (think its a auto scroll that messes screen height on scrolling at same time as keyboard changes height)
/// https://github.com/ionic-team/capacitor/issues/1366
if (Capacitor.isNativePlatform()) {
  Keyboard.setScroll({ isDisabled: true });
}

addPushListener();

async function onAppStateChange(isActive: boolean) {
  spLogTags("React App Version", ["UX"], import.meta.env.VITE_APP_VERSION);
  if (isSwitchingStage) return;

  if (import.meta.env.VITE_APP_VERSION)
    CapacitorUpdater.setCustomId({
      customId: import.meta.env.VITE_APP_VERSION,
    });

  if (isActive) {
    // App became active
    if (backgroundTimer) {
      clearTimeout(backgroundTimer);
      backgroundTimer = null;
    }
    bs().onOpen();
    // Another webview may have staged draft work while this one was hidden.
    void pullLatestDrafts();
    setTimeout(() => {
      bs().onReturn();
    }, 2000);
    // A local build would otherwise swap itself for the channel's bundle, running that
    // branch's code instead of the checkout's. Switching stage still downloads on request.
    if (!otaUpdatesEnabled()) {
      spLogTags("Skipping update check, OTA updates are off for this build", ["UPDATE"]);
      return;
    }
    spLogTags("Check for updates if isSwitchingStage === false", ["UPDATE"]);
    // Do the download during user active app time to prevent failed download
    const latest = await CapacitorUpdater.getLatest();
    spLogTags("latest version : ", ["UPDATE"], JSON.stringify(latest));
    if (latest.url && (await isNewerBundle(latest.version))) {
      try {
        data = await CapacitorUpdater.download({
          url: latest.url,
          version: latest.version,
        });

        if (latest.major) {
          spLogTags("force downloaded version : ", ["UPDATE"], JSON.stringify(data));

          // The swap reloads the webview, taking a link mid-resolution and the sign-up it leads
          // to with it. `applyUpdate` picks this bundle up on a later background instead.
          if (isDeeplinkInFlight()) {
            spLogTags("holding the forced update, a link is being resolved", ["UPDATE", "DEEPLINK"]);
          } else {
            // Bounded: a bundle that boots hides it on its first view (hideSplashOnce); a
            // bundle that never boots must not keep it up forever, so autoHide does.
            SplashScreen.show(SWAP_SPLASH_SETTINGS);
            try {
              await CapacitorUpdater.set(data);
            } catch (err) {
              console.log(err);
              SplashScreen.hide(); // in case the set fail, otherwise the new app will have to hide it
            }
          }
        }
        spLogTags("downloaded version : ", ["UPDATE"], JSON.stringify(data));
      } catch (e) {
        console.error("download failed", e);
      }
    }
  } else {
    captureSentryMessage("Closing app logs for " + (bs().profile?.email ?? "NO_USER"));

    bs().closeWebsocket(true, "appStateListener (inactive?)");

    // Land staged draft writes in shared storage before this webview can be torn down.
    void flushStoredWrites();

    useOverlayStore.getState().resetShownSnackList();
    // Create a timer to determine if this is a genuine background event
    // after the permission threshold has passed
    backgroundTimer = setTimeout(() => {
      // The pathname is the auth screen on web and Android; under native navigation the pre-auth
      // stack is a root of its own and the pending link is what names it.
      if (!window.location.pathname.startsWith("/auth") && !isDeeplinkInFlight()) applyUpdate();
      backgroundTimer = null;
    }, PERMISSION_THRESHOLD);
  }
}

async function applyUpdate() {
  if (data) {
    spLogTags("Set the new version", ["UPDATE"]);
    // Do the switch when user leave app or when you want
    // Bounded for the same reason as the forced swap above.
    SplashScreen.show(SWAP_SPLASH_SETTINGS);
    try {
      await CapacitorUpdater.set(data);
    } catch (err) {
      console.log(err);
      SplashScreen.hide(); // in case the set fail, otherwise the new app will have to hide it
    }
  }
}

/** Opens a URL the OS handed the app, from `appUrlOpen` or recovered from the launch URL. */
function openNativeUrl(raw: string): void {
  const url = new URL(raw);
  if (url.searchParams.has("stage")) {
    isSwitchingStage = true;
    console.log("set path before update : ", url.pathname + url.search);
    console.log(url.searchParams.get("stage") as string);

    switchStage(url.searchParams.get("stage") as string, url.pathname + url.search);
    return;
  }
  navigateToDeeplink(url.pathname + url.search);
}

// Long enough that a link `appUrlOpen` is going to deliver has arrived, so the recovery below
// only ever sees one the event missed. The resolver holds a link until the app can act on it,
// so arriving this late costs nothing.
const LAUNCH_URL_RECOVERY_DELAY = 2000;

export const registerListeners = async () => {
  await App.removeAllListeners();
  await LocalNotifications.removeAllListeners();
  await App.addListener("appStateChange", async ({ isActive }) => {
    bs().syncNotificationPermissions();
    // Re-check calendar permission on foreground too, so a revocation in OS settings is reflected.
    if (isActive) void checkCalInitialPermissions();
    onAppStateChange(isActive);
  });
  await App.addListener("appUrlOpen", (data) => {
    console.log("App opened with URL:", data);
    recordOpenedUrl(data.url);
    openNativeUrl(data.url);
  });
  // Tapping a local notification deeplinks to its item (path stored on the notification's extra).
  await LocalNotifications.addListener("localNotificationActionPerformed", (action) => {
    const path = action.notification.extra?.path;
    if (typeof path === "string" && path) {
      navigateToDeeplink(path);
    }
  });
  await CapacitorUpdater.addListener("appReady", async () => {
    console.log("appReady");

    /// timeouts for a rerender issue on android. Forces a rerender of page when entering app.
    /// reRender elem stored in scroll provider
    setTimeout(() => {
      const elem = document.getElementById("reRender");

      if (elem?.style) {
        elem.style.visibility = "visible";
        elem.style.opacity = "1";

        setTimeout(() => {
          elem.style.visibility = "hidden";
          elem.style.opacity = "0";
        }, 50);
      }
    }, 200);

    let { value } = await Preferences.get({ key: "afterUpdatePath" });
    if (value) {
      console.log("afterUpdatePath", value);
      Preferences.remove({ key: "afterUpdatePath" });
      setTimeout(() => {
        if (value?.startsWith("//sp")) value = value.replace("//sp", "");
        if (isIosNativeNavigation()) {
          navigateToDeeplink(value ?? "/");
        } else {
          window.location.href = window.location.origin + value;
        }
      }, 1000);
    }
  });
  await App.addListener("appRestoredResult", (data) => {
    console.log("Restored state:", data);
  });
  await App.addListener("backButton", ({ canGoBack }) => {
    /// Block back if drawer is open
    const allowBack = !useDrawerStore.getState().openState;
    if (!allowBack) {
      return;
    }

    if (!canGoBack) {
      App.exitApp();
    } else {
      const path = window.location.pathname;
      const backPath = backButtonMap[path];

      if (backPath === "exit") {
        App.exitApp();
        return;
      }
      if (!backPath) {
        window.history.back();
        return;
      }
      window.replacePath(backPath);
    }
  });
  setTimeout(() => void recoverLaunchUrl(openNativeUrl), LAUNCH_URL_RECOVERY_DELAY);
};

export const switchStage = async (stage: string, path?: string) => {
  const lowerStage = stage.toLowerCase();
  const currentStage = reactAppStage();
  console.log("currentStage", currentStage);

  if (currentStage === lowerStage) {
    if (path) {
      if (path.startsWith("//sp")) path = path.replace("//sp", "");
      if (isIosNativeNavigation()) {
        navigateToDeeplink(path);
      } else {
        window.location.href = window.location.origin + path;
      }
    }
    return;
  } else {
    Preferences.set({ key: "afterUpdatePath", value: path ?? "" });
  }
  localStorage.clear();
  localStorage.setItem("REACT_APP_STAGE", lowerStage);
  // A local build keeps its own code and only points it at the other stage's backend;
  // `afterUpdatePath` is picked up by the `appReady` listener once it has reloaded.
  if (!otaUpdatesEnabled()) {
    await Preferences.set({ key: "afterUpdatePath", value: path ?? "" });
    window.location.reload();
    return;
  }
  await switchReactBuild(lowerStage);
};

export const switchReactBuild = async (stage: string) => {
  if (Capacitor.isNativePlatform()) {
    console.log("switchStage", stage);
    SplashScreen.show(SWAP_SPLASH_SETTINGS);
    CapacitorUpdater.setCustomId({
      customId: "0.0.0.0-" + stage,
    });
    console.log("customId setted ", "0.0.0.0-" + stage);
    onReactBuildChange();
  }
};

export const switchReactToSpecificBuild = async (customId: string) => {
  if (Capacitor.isNativePlatform()) {
    console.log("switchStage", customId);
    SplashScreen.show(SWAP_SPLASH_SETTINGS);
    CapacitorUpdater.setCustomId({
      customId: customId,
    });
    console.log("customId setted ", customId);
    onReactBuildChange();
  }
};

async function onReactBuildChange() {
  try {
    const latest = await CapacitorUpdater.getLatest();
    console.log("latest received:" + JSON.stringify(latest));
    if (latest.url) {
      const newUpdate = await CapacitorUpdater.download({
        url: latest.url,
        version: latest.version,
      });
      console.log("Download new bundle:", JSON.stringify(newUpdate));
      try {
        await CapacitorUpdater.set({ id: newUpdate.id });
      } catch (err) {
        console.log("switchStage done");

        isSwitchingStage = false;
        console.log(err);
        await SplashScreen.hide(); // in case the set fail, otherwise the new app will have to hide it
      }
    }
  } catch (error) {
    console.log("error on getting update ", error);
    await SplashScreen.hide();
    return;
  }
}

onAppStateChange(true);
